/** Copyright 2025 SAP SE or an SAP affiliate company. All rights reserved. */
import { useAppDispatch } from '@/hooks';
import { loadingActions } from '@/store/loadingSlice';
import axiosInstance, { LocalStorageKeys } from '@/utils/axiosInstance';
import * as storageUtils from '@/utils/storageUtils';
import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

const TIMEOUTS = {
    INACTIVITY_LIMIT: 40 * 60 * 1000,
    WARNING_TIME: 30 * 60 * 1000,
    ONE_MINUTE: 60 * 1000,
    DEBOUNCING_DELAY: 500,
} as const;

const ACTIVITY_EVENTS = ['mousemove', 'click', 'keydown', 'wheel', 'touchstart', 'touchmove'] as const;

interface RefreshTokenResponse {
    access_token: string;
    refresh_token: string;
}

const useSessionManager = (authToken: string | null, refreshToken: string | null) => {
    const [showWarning, setShowWarning] = useState(false);
    const [remainingTime, setRemainingTime] = useState(TIMEOUTS.INACTIVITY_LIMIT);
    const inactivityTimerRef = useRef<NodeJS.Timeout | undefined>();
    const intervalRef = useRef<NodeJS.Timeout | undefined>();
    const debounceTimerRef = useRef<NodeJS.Timeout | undefined>();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const resetTimer = useCallback(() => {
        clearTimeout(inactivityTimerRef.current);
        clearInterval(intervalRef.current);
        setRemainingTime(TIMEOUTS.INACTIVITY_LIMIT);
        intervalRef.current = setInterval(() => {
            setRemainingTime(prev => {
                const newTime = Math.max(prev - TIMEOUTS.ONE_MINUTE, 0);
                if (newTime === 0) {
                    clearInterval(intervalRef.current);
                    storageUtils.removeItem(['refreshToken', 'token', 'user', 'code', 'state']);
                }

                return newTime;
            });
        }, TIMEOUTS.ONE_MINUTE);

        inactivityTimerRef.current = setTimeout(() => {
            setShowWarning(true);
        }, TIMEOUTS.WARNING_TIME);
    }, []);

    const loginSession = useCallback(() => {
        setShowWarning(false);
        navigate('/login');
    }, []);

    const signoutSession = useCallback(async () => {
        clearTimeout(inactivityTimerRef.current);
        clearInterval(intervalRef.current);
        setShowWarning(false);
        dispatch(loadingActions.startLoading());

        try {
            await axiosInstance.get(`/logout`);
            storageUtils.removeItem(['refreshToken', 'token', 'user', 'code', 'state']);
        } catch (error) {
            console.error('Error logging out:', error);
        } finally {
            dispatch(loadingActions.stopLoading());
            navigate('/login');
        }
    }, [dispatch]);

    const closeSession = useCallback(() => {
        setShowWarning(false);
    }, []);

    const extendSession = useCallback(async () => {
        if (!refreshToken) return;

        clearTimeout(inactivityTimerRef.current);
        clearInterval(intervalRef.current);
        setShowWarning(false);
        dispatch(loadingActions.startLoading());
        try {
            const response = await axiosInstance.post<RefreshTokenResponse>(`token?refreshToken=${refreshToken}`);
            storageUtils.setItem(LocalStorageKeys.ACCESS_TOKEN, response.data.access_token);
            storageUtils.setItem(LocalStorageKeys.REFRESH_TOKEN, response.data.refresh_token);
        } catch (error) {
            console.error('Failed to refresh token:', error);
            storageUtils.removeItem([LocalStorageKeys.ACCESS_TOKEN]);
        } finally {
            dispatch(loadingActions.stopLoading());

            resetTimer();
        }
    }, [dispatch, refreshToken]);

    const debouncedHandleActivity = useCallback(() => {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = setTimeout(() => {
            resetTimer();
        }, TIMEOUTS.DEBOUNCING_DELAY); // 500ms debounce delay
    }, [resetTimer]);

    const handleActivity = useCallback(() => {
        resetTimer();
    }, [resetTimer]);

    useEffect(() => {
        if (!authToken) return;
        if (showWarning) {
            ACTIVITY_EVENTS.forEach(event => {
                document.removeEventListener(event, handleActivity);
                document.removeEventListener(event, debouncedHandleActivity);
            });
        } else {
            ACTIVITY_EVENTS.forEach(event => {
                if (event === 'mousemove' || event === 'wheel') {
                    document.addEventListener(event, debouncedHandleActivity, { passive: true });
                } else {
                    document.addEventListener(event, handleActivity, { passive: true });
                }
            });
            resetTimer();
        }

        return () => {
            clearTimeout(debounceTimerRef.current);
            ACTIVITY_EVENTS.forEach(event => {
                document.removeEventListener(event, handleActivity);
                document.removeEventListener(event, debouncedHandleActivity);
            });
        };
    }, [authToken, handleActivity, debouncedHandleActivity, showWarning]);

    return {
        showWarning,
        closeSession,
        remainingTime,
        extendSession,
        loginSession,
        signoutSession,
    };
};

export default useSessionManager;
